import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../api.js';
import { authClient, useSession } from '../auth-client.js';
import { useActor } from '../lib/useActor.js';
import { isTester } from '../lib/testers.js';
import { useOwnParticipantId } from '../lib/useOwnParticipantId.js';
import Avatar from './Avatar.js';
import LanguageToggle from './LanguageToggle.js';
import styles from './AppHeader.module.css';

/**
 * Identity and settings, on every signed-in screen.
 *
 * When rendered on a screen scoped to a bolão, pass both `groupId` and
 * `poolId` so the identity block links to the viewer's own profile within
 * that bolão — clicking your name/avatar in the top-left corner takes you
 * there. Circle membership and bolão participation are different lists now,
 * so "your own profile" can only be resolved from the bolão's own
 * participant list (`GET /groups/:id/bolaos/:poolId`), not the circle
 * roster. With no `poolId` (e.g. the circle home, or the groups list), it
 * links to `/settings` instead — there's no global profile page, but
 * "manage my account" is still the closest match for what tapping your own
 * name/photo should do, and leaving it unlinked there reads as broken
 * rather than intentional.
 *
 * The language switch used to live directly in this corner; it now lives on
 * the settings screen this links to, which is also where it persists across
 * devices instead of just this browser (PLAN.md Phase 7).
 *
 * A guest — someone playing this one circle by link and PIN (PLAN.md Phase
 * 10.29) — gets the same header minus the parts that assume an account:
 * settings is an account screen and is dropped rather than shown broken, and
 * signing out ends the guest session instead of Better Auth's.
 */
export default function AppHeader({ groupId, poolId }: { groupId?: string; poolId?: string }) {
  const { t } = useTranslation();
  const { actor, isGuest } = useActor();
  const { data: session } = useSession();
  const { data: pool } = useQuery({
    queryKey: ['pool', groupId, poolId],
    queryFn: () => api.getPool(groupId!, poolId!),
    enabled: !!groupId && !!poolId,
  });

  const ownParticipantId = useOwnParticipantId(groupId, poolId, pool);
  const name = actor?.name ?? '';
  const imageUrl = actor?.imageUrl ?? null;

  const identity = (
    <>
      <Avatar name={name} imageUrl={imageUrl} size={28} />
      <span className={styles.name}>{name}</span>
    </>
  );

  // A guest with no bolão in view has nowhere to go: `/settings` is an
  // account screen, and sending them there only to be bounced back is worse
  // than a name that simply isn't a link.
  const identityTarget = ownParticipantId
    ? `/groups/${groupId}/bolaos/${poolId}/participants/${ownParticipantId}`
    : isGuest
      ? null
      : '/settings';

  const signOut = () => {
    if (isGuest) {
      void api.signOutGuest().then(() => window.location.assign('/'));
      return;
    }
    void authClient.signOut().then(() => window.location.assign('/'));
  };

  return (
    <header className={styles.header}>
      {identityTarget ? (
        <Link to={identityTarget} className={styles.identityLink}>
          {identity}
        </Link>
      ) : (
        <span className={styles.identityLink}>{identity}</span>
      )}

      <div className={styles.actions}>
        {isGuest ? (
          // The language switch normally lives on the settings screen, which a
          // guest cannot reach — so without this the app would be stuck in
          // whatever language the browser guessed, for exactly the people
          // least likely to work around it.
          <LanguageToggle />
        ) : (
          <>
            {isTester(session?.user?.email) ? (
              // Internal-only, not translated — see lib/testers.ts and
              // FaceLandmarksExperimentScreen's own doc comment for why.
              <Link
                to="/experiments/face-landmarks"
                className={styles.settingsLink}
                aria-label="Face landmarks test"
              >
                🧪
              </Link>
            ) : null}
            <Link to="/settings" className={styles.settingsLink} aria-label={t('settings.title')}>
              {t('settings.icon')}
            </Link>
          </>
        )}
        <button type="button" className={styles.signOut} onClick={signOut}>
          {t('auth.signOut')}
        </button>
      </div>
    </header>
  );
}
